<template>
  <div
    class="ai-stream-md ai-stream-md--math"
    :aria-busy="showTypingDots ? 'true' : 'false'"
  >
    <!-- markdown-it 未就绪时先纯文本；就绪后边流式边 md.render+KaTeX（errorColor 用 currentColor 减轻半截公式红闪） -->
    <p
      v-if="!mdReady"
      class="ai-msg ai-msg--assistant ai-msg--assistant-fragment"
    >{{ displayText }}<span
      v-if="showTypingDots"
      class="ai-stream-md__typing-dots"
      aria-hidden="true"
    >{{ typingDotsLabel }}</span></p>
    <div
      v-else
      class="ai-stream-md__rich-with-dots"
    >
      <div
        ref="aiMdBody"
        class="ai-msg ai-msg--assistant markdown-body ai-stream-md__body"
        v-html="sanitizedHtml"
      />
    </div>
  </div>
</template>

<script>
/**
 * 与 bloom-H5 asa-streaming-text 一致：streaming 为 true 时用 rAF 小步追上完整 text；关闭后立刻全文。
 * 展示层在 markdown-it 就绪后将正文渲染为 Markdown（表格、任务列表、删除线、脚注、定义列表、KaTeX、GFM ``` 代码围栏等），HTML 经 aiAssistantMarkdownXss 过滤。
 * 伴学业务围栏（```contest``` / ```topicTree``` / ```topicNameListStr```）在 header-box 拆片时已剔除，此处仅渲染正文段内普通 GFM 围栏；缩进代码块仍禁用（规则名 code），避免与列表缩进冲突。
 * 行内公式使用 markdown-it-katex-spaced，允许 `$ f(x) $` 等 $ 两侧空格。
 * 流式过程中同步增量 md.render；KaTeX errorColor 用 currentColor，减轻未闭合公式时的显眼红字。
 * 与 bloom-H5 一致：typingDotsActive + streaming 时在文末循环「. / .. / ...」，并以原生节点插入末段 p 内以免换行。
 */
import { aiAssistantMarkdownXss } from '../../assets/config/xss.js'

export default {
  name: 'AiStreamingText',
  props: {
    text: {
      type: String,
      default: ''
    },
    streaming: {
      type: Boolean,
      default: false
    },
    /** 与 header-box agentResPartShowsStreamTypingDots 一致：仅最后一个文案片段为 true */
    typingDotsActive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      displayText: '',
      sanitizedHtml: '',
      mdReady: false,
      rafId: null,
      streamTickRafId: null,
      _streamGrowthAt: 0,
      _lastStreamTargetLen: 0,
      _mdRafId: null,
      _md: null,
      _xss: null,
      _mdInitPromise: null,
      typingDotsPhase: 0,
      _typingDotsTimer: null,
      /** v-html 会清空 md 容器，省略号用原生节点插入末段内（与 bloom-H5 asa-streaming-text 一致） */
      _typingDotsDom: null
    }
  },
  computed: {
    showTypingDots () {
      return !!(this.typingDotsActive && this.streaming)
    },
    typingDotsLabel () {
      const seq = ['.', '..', '...']
      return seq[this.typingDotsPhase % 3]
    }
  },
  watch: {
    text: 'onTextOrModeChange',
    streaming: 'onTextOrModeChange',
    displayText () {
      this.scheduleMarkdownRefresh()
    },
    showTypingDots: {
      handler: 'onShowTypingDotsChange',
      immediate: true
    }
  },
  mounted () {
    this._xss = aiAssistantMarkdownXss()
    this.onTextOrModeChange()
    this.initMarkdownEngine()
  },
  beforeDestroy () {
    this.cancelRaf()
    this.cancelMdRaf()
    this._detachTypingDotsDom()
    this._clearTypingDotsTimer()
    if (this.streamTickRafId != null) {
      cancelAnimationFrame(this.streamTickRafId)
      this.streamTickRafId = null
    }
  },
  methods: {
    _clearTypingDotsTimer () {
      if (this._typingDotsTimer != null) {
        clearInterval(this._typingDotsTimer)
        this._typingDotsTimer = null
      }
    },
    onShowTypingDotsChange (on) {
      this._clearTypingDotsTimer()
      if (!on) {
        this.typingDotsPhase = 0
        this._detachTypingDotsDom()
        return
      }
      this.typingDotsPhase = 0
      this._typingDotsTimer = setInterval(() => {
        this.typingDotsPhase = (this.typingDotsPhase + 1) % 3
        this._updateTypingDotsLabelInDom()
      }, 450)
      this.$nextTick(() => {
        this._attachTypingDotsDom()
      })
    },
    _detachTypingDotsDom () {
      if (this._typingDotsDom && this._typingDotsDom.parentNode) {
        this._typingDotsDom.parentNode.removeChild(this._typingDotsDom)
      }
      this._typingDotsDom = null
    },
    _findTypingDotsAnchorEl (body) {
      if (!body || !body.children || !body.children.length) return null
      const lc = body.lastElementChild
      if (!lc) return null
      const lt = lc.tagName
      if (lt === 'P') return lc
      if (lt === 'UL' || lt === 'OL') {
        const lis = lc.children
        for (let i = lis.length - 1; i >= 0; i--) {
          const li = lis[i]
          if (li && li.tagName === 'LI') return li
        }
      }
      if (lt === 'BLOCKQUOTE') {
        const ps = lc.getElementsByTagName('p')
        if (ps.length) return ps[ps.length - 1]
        return lc
      }
      for (let i = body.children.length - 1; i >= 0; i--) {
        const el = body.children[i]
        if (el && el.tagName === 'P') return el
      }
      return null
    },
    _updateTypingDotsLabelInDom () {
      if (this._typingDotsDom) {
        this._typingDotsDom.textContent = this.typingDotsLabel
      }
    },
    _attachTypingDotsDom () {
      this._detachTypingDotsDom()
      if (!this.showTypingDots || !this.mdReady) return
      const root = this.$refs.aiMdBody
      if (!root) return
      let anchor = this._findTypingDotsAnchorEl(root)
      if (!anchor) {
        const le = root.lastElementChild
        const bad = new Set(['TABLE', 'PRE', 'HR'])
        if (le && !bad.has(le.tagName)) anchor = le
      }
      if (!anchor) return
      const span = document.createElement('span')
      span.className = 'ai-stream-md__typing-dots'
      span.setAttribute('aria-hidden', 'true')
      span.textContent = this.typingDotsLabel
      anchor.appendChild(span)
      this._typingDotsDom = span
      this.emitLayout()
    },
    emitLayout () {
      if (this.streamTickRafId != null) return
      this.streamTickRafId = requestAnimationFrame(() => {
        this.streamTickRafId = null
        this.$emit('stream-tick')
      })
    },
    cancelMdRaf () {
      if (this._mdRafId != null) {
        cancelAnimationFrame(this._mdRafId)
        this._mdRafId = null
      }
    },
    scheduleMarkdownRefresh () {
      if (!this.mdReady || !this._md || !this._xss) return
      this.cancelMdRaf()
      this.flushMarkdownHtml()
    },
    flushMarkdownHtml () {
      this._detachTypingDotsDom()
      if (!this._md || !this._xss) return
      const raw = this._md.render(this.displayText != null ? String(this.displayText) : '')
      this.sanitizedHtml = this._xss.process(raw)
      this.emitLayout()
      this.$nextTick(() => {
        this._attachTypingDotsDom()
      })
    },
    async initMarkdownEngine () {
      if (this._mdInitPromise) return this._mdInitPromise
      this._mdInitPromise = (async () => {
        try {
          await Promise.all([
            import(/* webpackChunkName: "github-markdown" */ 'github-markdown-css/github-markdown-light.css'),
            import(/* webpackChunkName: "katex-css" */ 'katex/dist/katex.min.css')
          ])
          const [
            mdMod,
            tableMod,
            footnoteMod,
            deflistMod,
            katexMod,
            taskListsMod,
            strikethroughMod
          ] = await Promise.all([
            import(/* webpackChunkName: "markdown-it" */ 'markdown-it'),
            import(/* webpackChunkName: "markdown-it-multimd-table" */ 'markdown-it-multimd-table'),
            import(/* webpackChunkName: "markdown-it-footnote" */ 'markdown-it-footnote'),
            import(/* webpackChunkName: "markdown-it-deflist" */ 'markdown-it-deflist'),
            import(/* webpackChunkName: "markdown-it-katex-spaced" */ '../../assets/js/markdown-it-katex-spaced.js'),
            import(/* webpackChunkName: "markdown-it-task-lists" */ 'markdown-it-task-lists'),
            import(/* webpackChunkName: "markdown-it-strike" */ '../../assets/js/markdown-it-strikethrough-gfm.js')
          ])
          const MarkdownIt = mdMod.default || mdMod
          const multimdTable = tableMod.default || tableMod
          const footnote = footnoteMod.default || footnoteMod
          const deflist = deflistMod.default || deflistMod
          const katexPlugin = katexMod.default || katexMod
          const taskLists = taskListsMod.default || taskListsMod
          const strikethroughGfm = strikethroughMod.default || strikethroughMod

          const md = new MarkdownIt({
            html: false,
            linkify: true,
            typographer: true,
            breaks: true
          })
          md.use(footnote)
          md.use(deflist)
          md.use(multimdTable, {
            multiline: true,
            rowspan: true,
            headerless: true
          })
          md.use(katexPlugin, {
            throwOnError: false,
            /* 与正文同色，避免流式半截公式时 error 红字闪烁 */
            errorColor: 'currentColor',
            /* 不输出 MathML 片段，减少 XSS 面且避免与过滤后的 DOM 不一致 */
            output: 'html'
          })
          md.use(strikethroughGfm)
          md.use(taskLists, { enabled: false, label: false })
          /* 缩进代码块规则名为 code（非 code_block）；保留禁用以免与列表缩进冲突。GFM 三反引号围栏需启用 fence 以展示 IM 返回的代码块 */
          md.disable(['code'])
          this._md = md
          this.mdReady = true
          this.flushMarkdownHtml()
        } catch (e) {
          console.warn('[AiStreamingText] markdown engine init failed', e)
          this.mdReady = false
        }
      })()
      return this._mdInitPromise
    },
    onTextOrModeChange () {
      if (!this.streaming) {
        this.cancelRaf()
        this.displayText = this.text != null ? String(this.text) : ''
        this._lastStreamTargetLen = this.displayText.length
        this.emitLayout()
        this.scheduleMarkdownRefresh()
        return
      }
      const full = this.text != null ? String(this.text) : ''
      if (full.length > this._lastStreamTargetLen) {
        this._streamGrowthAt = Date.now()
      }
      this._lastStreamTargetLen = full.length
      if (this.displayText.length > full.length) {
        this.displayText = full
      }
      this.scheduleCatchUp()
    },
    cancelRaf () {
      if (this.rafId != null) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
    },
    scheduleCatchUp () {
      if (!this.streaming) return
      if (this.rafId != null) return
      const tick = () => {
        this.rafId = null
        if (!this.streaming) {
          this.displayText = this.text != null ? String(this.text) : ''
          this.scheduleMarkdownRefresh()
          return
        }
        const full = this.text != null ? String(this.text) : ''
        if (this.displayText.length >= full.length) {
          return
        }
        const lag = full.length - this.displayText.length
        const targetGrowingRecently = Date.now() - this._streamGrowthAt < 220
        let step
        if (targetGrowingRecently) {
          step = Math.min(4, Math.max(1, Math.ceil(lag / 48)))
        } else {
          step =
            lag > 120 ? Math.min(20, Math.ceil(lag / 12)) : lag > 40 ? 6 : lag > 12 ? 2 : 1
        }
        this.displayText = full.slice(0, this.displayText.length + step)
        this.emitLayout()
        if (this.displayText.length < full.length) {
          this.rafId = requestAnimationFrame(tick)
        }
      }
      this.rafId = requestAnimationFrame(tick)
    }
  }
}
</script>

<style scoped>
.ai-stream-md {
  min-width: 0;
  max-width: 100%;
  /* 不在单条消息上创建滚动容器：上下滚动仅由外层 .ai-chat-scroll 负责；
     若设 overflow-x:auto，规范会将 overflow-y 视为 auto，易出现消息内上下滑动 */
  overflow: visible;
}

/* markdown-it 未就绪时的纯文本段落：与正文区一致 16px */
.ai-stream-md .ai-msg--assistant-fragment {
  font-size: 16px;
  line-height: 1.65;
}

.ai-stream-md__rich-with-dots {
  max-width: 100%;
  min-width: 0;
}

.ai-stream-md__typing-dots {
  display: inline;
  font-size: inherit;
  line-height: inherit;
  color: #8b8b8b;
  white-space: nowrap;
  vertical-align: baseline;
}

/*
 * 站内其它路由（如认证页）会异步加载 github-markdown.css（auto），其在 prefers-color-scheme:dark 下为全局 .markdown-body 写入深色变量，
 * 且与 github-markdown-light.css 共用同等选择器优先级时以后加载为准，伴学问答可能被覆盖为深色。
 * 双类选择器优先级更高，锁定 github-markdown「浅色」语义 Token（与官方 light media 块一致）。
 */
.ai-stream-md .markdown-body {
  color-scheme: light;
  --focus-outlineColor: #0969da;
  --fgColor-default: #1f2328;
  --fgColor-muted: #59636e;
  --fgColor-accent: #0969da;
  --fgColor-success: #1a7f37;
  --fgColor-attention: #9a6700;
  --fgColor-danger: #d1242f;
  --fgColor-done: #8250df;
  --bgColor-default: #ffffff;
  --bgColor-muted: #f6f8fa;
  --bgColor-neutral-muted: #818b981f;
  --bgColor-attention-muted: #fff8c5;
  --borderColor-default: #d1d9e0;
  --borderColor-muted: #d1d9e0b3;
  --borderColor-neutral-muted: #d1d9e0b3;
  --borderColor-accent-emphasis: #0969da;
  --borderColor-success-emphasis: #1a7f37;
  --borderColor-attention-emphasis: #9a6700;
  --borderColor-danger-emphasis: #cf222e;
  --borderColor-done-emphasis: #8250df;
  --color-prettylights-syntax-comment: #59636e;
  --color-prettylights-syntax-constant: #0550ae;
  --color-prettylights-syntax-constant-other-reference-link: #0a3069;
  --color-prettylights-syntax-entity: #6639ba;
  --color-prettylights-syntax-storage-modifier-import: #1f2328;
  --color-prettylights-syntax-entity-tag: #0550ae;
  --color-prettylights-syntax-keyword: #cf222e;
  --color-prettylights-syntax-string: #0a3069;
  --color-prettylights-syntax-variable: #953800;
  --color-prettylights-syntax-brackethighlighter-unmatched: #82071e;
  --color-prettylights-syntax-brackethighlighter-angle: #59636e;
  --color-prettylights-syntax-invalid-illegal-text: #f6f8fa;
  --color-prettylights-syntax-invalid-illegal-bg: #82071e;
  --color-prettylights-syntax-carriage-return-text: #f6f8fa;
  --color-prettylights-syntax-carriage-return-bg: #cf222e;
  --color-prettylights-syntax-string-regexp: #116329;
  --color-prettylights-syntax-markup-list: #3b2300;
  --color-prettylights-syntax-markup-heading: #0550ae;
  --color-prettylights-syntax-markup-italic: #1f2328;
  --color-prettylights-syntax-markup-bold: #1f2328;
  --color-prettylights-syntax-markup-deleted-text: #82071e;
  --color-prettylights-syntax-markup-deleted-bg: #ffebe9;
  --color-prettylights-syntax-markup-inserted-text: #116329;
  --color-prettylights-syntax-markup-inserted-bg: #dafbe1;
  --color-prettylights-syntax-markup-changed-text: #953800;
  --color-prettylights-syntax-markup-changed-bg: #ffd8b5;
  --color-prettylights-syntax-markup-ignored-text: #d1d9e0;
  --color-prettylights-syntax-markup-ignored-bg: #0550ae;
  --color-prettylights-syntax-meta-diff-range: #8250df;
  --color-prettylights-syntax-sublimelinter-gutter-mark: #818b98;
  color: #1f2328;
  background-color: #ffffff;
}

.ai-stream-md__body {
  font-size: 16px;
  line-height: 1.65;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}
/* 块级/行内公式均需要足够行高，避免积分号等被行盒裁切 */
.ai-stream-md__body >>> p {
  margin: 0.5em 0;
  line-height: 1.8;
  overflow: visible;
}
.ai-stream-md__body >>> p:first-child {
  margin-top: 0;
}
.ai-stream-md__body >>> p:last-child {
  margin-bottom: 0;
}
.ai-stream-md__body >>> table {
  display: block;
  width: max-content;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin: 0.5em 0;
  font-size: 14px;
}
.ai-stream-md__body >>> pre {
  overflow-x: auto;
  max-width: 100%;
  font-size: 13px;
  padding: 0.75em 1em;
  margin: 0.5em 0;
  border-radius: 6px;
  background: rgba(27, 31, 35, 0.05);
}
.ai-stream-md__body >>> pre code {
  font-size: inherit;
  white-space: pre;
  word-break: normal;
  overflow-wrap: normal;
}
.ai-stream-md__body >>> h1,
.ai-stream-md__body >>> h2,
.ai-stream-md__body >>> h3 {
  margin-top: 0.6em;
  margin-bottom: 0.35em;
  font-size: 1.1em;
  line-height: 1.35;
}
.ai-stream-md__body >>> ul,
.ai-stream-md__body >>> ol {
  margin: 0.35em 0;
  padding-left: 1.25em;
}
.ai-stream-md__body >>> li {
  line-height: 1.8;
  overflow: visible;
}
.ai-stream-md__body >>> .contains-task-list {
  list-style: none;
  padding-left: 1.2em;
}
.ai-stream-md__body >>> .task-list-item-checkbox {
  margin-right: 0.35em;
  vertical-align: middle;
}
.ai-stream-md__body >>> dl {
  margin: 0.5em 0;
}
.ai-stream-md__body >>> dt {
  font-weight: 600;
  margin-top: 0.35em;
}
.ai-stream-md__body >>> dd {
  margin: 0.15em 0 0.35em 1em;
}
.ai-stream-md__body >>> .footnotes {
  margin-top: 0.75em;
  padding-top: 0.5em;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 13px;
}
/* KaTeX：勿对 .katex-display 仅设 overflow-x:auto；word-break/line-height 在无 scoped 段对 .katex 统一处理 */
.ai-stream-md__body >>> .katex {
  font-size: 1em;
  overflow: visible;
  text-rendering: auto;
}
.ai-stream-md__body >>> .katex-display {
  display: block;
  margin: 1em 0;
  padding: 0.15em 0 0.35em;
  max-width: 100%;
  overflow: visible;
}
.ai-stream-md__body >>> .katex-display > .katex {
  max-width: 100%;
  overflow: visible;
}
</style>

<style>
/* 插入在 v-html 内的省略号无 scoped data-v（与 bloom-H5 一致） */
.ai-stream-md--math .markdown-body .ai-stream-md__typing-dots {
  display: inline;
  font-size: inherit;
  line-height: inherit;
  color: #8b8b8b;
  white-space: nowrap;
  vertical-align: baseline;
}

/*
 * 无 scoped：避免 [data-v-xxx] 抬权压过 KaTeX 的 .katex-display > .katex。
 * 仅作用于 .ai-stream-md--math，不影响站内其他 Markdown。
 */
.ai-stream-md--math .markdown-body .katex-display {
  margin-top: 1.15em !important;
  margin-bottom: 1.45em !important;
  padding-top: 0.2em !important;
  padding-bottom: 0.55em !important;
  overflow: visible !important;
}
.ai-stream-md--math .markdown-body .katex-display > .katex {
  display: block !important;
  text-align: center !important;
  white-space: nowrap !important;
}
/*
 * 阻断 .ai-stream-md__body 的 word-break / 过大 line-height 渗入公式内部，
 * 避免格林公式类大式子上下标、分式错位（勿对 .katex 设 vertical-align:middle，复杂行内会乱）。
 * 根节点 line-height 与 katex.css 中 .katex 一致。
 */
.ai-stream-md--math .markdown-body .katex {
  word-break: normal;
  overflow-wrap: normal;
  line-height: 1.2;
}
.ai-stream-md--math .markdown-body .katex * {
  word-break: normal;
  overflow-wrap: normal;
}
/* markdown-it-katex 块级输出为 <p><span class="katex-display">…</span></p>，只调段距，不改 line-height 以免干扰块级公式 */
.ai-stream-md--math .markdown-body p:has(> .katex-display:only-child) {
  margin-top: 1.15em !important;
  margin-bottom: 1.45em !important;
}
</style>



// WEBPACK FOOTER //
// src/components/public/ai-streaming-text.vue